import { useMemo } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { LineChart, type LineSeries } from "@mui/x-charts/LineChart";
import type { AxisValueFormatterContext } from "@mui/x-charts/models";
import ChartContainer from "../common/ChartContainer";
import { formatAxisDate } from "../../utils/chartDate";
import {
  STATUS_COLORS,
  STATUS_LABELS,
  type StatusChartResponse,
} from "./alertStatusChart";

// Amber for the percentage line — stands apart from the emerald and blue
// count lines without clashing.
const AUTO_PERCENT_COLOR = "#f59e0b"; // amber-500

interface ResolutionsLineChartProps {
  title: string;
  data: StatusChartResponse | null;
  loading: boolean;
  // Raw status labels whose daily counts sum into the "auto" line. Defaults match the auto-refund flow.
  autoLabels?: string[];
  // Raw status labels whose daily counts sum into the "manual" line. Defaults match the auto-refund flow.
  manualLabels?: string[];
  // Display label for the auto line.
  autoSeriesLabel?: string;
  // Display label for the manual line.
  manualSeriesLabel?: string;
  // Raw status labels for alerts that reached no resolution. Omit to leave the line off entirely;
  // when set they get their own line AND join the manual side of the automation-rate denominator.
  unresolvedLabels?: string[];
  // Display label for the unresolved line.
  unresolvedSeriesLabel?: string;
}

const DEFAULT_AUTO_LABELS = ["success", "cancelled"];
const DEFAULT_MANUAL_LABELS = ["manual_success"];

// Lighter shade of STATUS_COLORS.success — keeps auto-real and auto-dry visually related.
const AUTO_DRY_COLOR = "#6ee7b7"; // emerald-300

// Red for the unresolved line — reads as backlog against the emerald/blue resolved lines.
const UNRESOLVED_COLOR = "#ef4444"; // red-500

// Stable series id so we can target the line's <path> with sx and apply a dashed stroke —
// MUI x-charts v8 has no per-series dash prop on LineSeriesType.
const AUTO_DRY_SERIES_ID = "subres-auto-dry";

interface DryRunFilter {
  // undefined = match any series with the label (sums real + dry)
  // true/false = match only series whose dryRun field equals this value
  dryRun?: boolean;
}

function matchesFilter(series: StatusChartResponse["chart"]["series"][number], label: string, filter: DryRunFilter): boolean {
  if (series.label !== label) return false;
  if (filter.dryRun === undefined) return true;
  // For backends that don't emit `dryRun`, treat absence as "real" so unsplit charts behave as before.
  return Boolean(series.dryRun) === filter.dryRun;
}

function seriesDataByLabel(data: StatusChartResponse, label: string, filter: DryRunFilter = {}): number[] {
  const len = data.chart.xAxis.length;
  // Multiple series can share a label when split by dry_run — sum them.
  return data.chart.series
    .filter((s) => matchesFilter(s, label, filter))
    .reduce((acc, s) => acc.map((v, i) => v + (s.data[i] ?? 0)), new Array(len).fill(0));
}

function sumSeriesDataByLabels(data: StatusChartResponse, labels: string[], filter: DryRunFilter = {}): number[] {
  return labels
    .map((label) => seriesDataByLabel(data, label, filter))
    .reduce((acc, arr) => acc.map((v, i) => v + arr[i]), new Array(data.chart.xAxis.length).fill(0));
}

function hasDryRunSeries(data: StatusChartResponse, labels: string[]): boolean {
  return data.chart.series.some((s) => labels.includes(s.label) && s.dryRun === true);
}

export function ResolutionsLineChart({
  title,
  data,
  loading,
  autoLabels = DEFAULT_AUTO_LABELS,
  manualLabels = DEFAULT_MANUAL_LABELS,
  autoSeriesLabel = "Auto-Resolved",
  manualSeriesLabel = STATUS_LABELS.manual_success,
  unresolvedLabels,
  unresolvedSeriesLabel = "Action Needed",
}: ResolutionsLineChartProps) {
  const theme = useTheme();

  const { series, xAxisData, hasData } = useMemo(() => {
    if (!data || data.chart.xAxis.length === 0) {
      return { series: [] as LineSeries[], xAxisData: [] as string[], hasData: false };
    }

    // When the backend has split auto rows by dry_run we render real and dry as separate lines
    // and exclude dry from the automation-rate metric. Otherwise behave as before (single auto line).
    const splitAutoByDryRun = hasDryRunSeries(data, autoLabels);

    const autoReal = splitAutoByDryRun
      ? sumSeriesDataByLabels(data, autoLabels, { dryRun: false })
      : sumSeriesDataByLabels(data, autoLabels);
    const autoDry = splitAutoByDryRun ? sumSeriesDataByLabels(data, autoLabels, { dryRun: true }) : null;
    const manual = sumSeriesDataByLabels(data, manualLabels);
    const unresolved = unresolvedLabels ? sumSeriesDataByLabels(data, unresolvedLabels) : null;

    // Automation rate uses only real auto in numerator and denominator — simulated runs don't count.
    // Unresolved alerts sit on the manual side: they are alerts automation did not resolve.
    const autoPercent = autoReal.map((a, i) => {
      const total = a + manual[i] + (unresolved?.[i] ?? 0);
      return total > 0 ? Math.round((a / total) * 1000) / 10 : null;
    });

    const realLabel = splitAutoByDryRun ? `${autoSeriesLabel} (Real)` : autoSeriesLabel;

    const countSeries: LineSeries[] = [
      {
        data: autoReal,
        label: realLabel,
        color: STATUS_COLORS.success,
        yAxisId: "count",
        curve: "monotoneX",
        showMark: false,
        valueFormatter: (v) => (v == null ? null : v.toLocaleString()),
      },
    ];

    if (autoDry) {
      countSeries.push({
        id: AUTO_DRY_SERIES_ID,
        data: autoDry,
        label: `${autoSeriesLabel} (Dry-Run)`,
        color: AUTO_DRY_COLOR,
        yAxisId: "count",
        curve: "monotoneX",
        showMark: false,
        valueFormatter: (v) => (v == null ? null : v.toLocaleString()),
      });
    }

    countSeries.push({
      data: manual,
      label: manualSeriesLabel,
      color: STATUS_COLORS.manual_success,
      yAxisId: "count",
      curve: "monotoneX",
      showMark: false,
      valueFormatter: (v) => (v == null ? null : v.toLocaleString()),
    });

    if (unresolved) {
      countSeries.push({
        data: unresolved,
        label: unresolvedSeriesLabel,
        color: UNRESOLVED_COLOR,
        yAxisId: "count",
        curve: "monotoneX",
        showMark: false,
        valueFormatter: (v) => (v == null ? null : v.toLocaleString()),
      });
    }

    const percentSeries: LineSeries = {
      data: autoPercent,
      label: "Automation Rate %",
      color: AUTO_PERCENT_COLOR,
      yAxisId: "percent",
      curve: "monotoneX",
      connectNulls: false,
      showMark: false,
      valueFormatter: (v) => (v == null ? null : `${v.toFixed(0)}%`),
    };

    return {
      series: [...countSeries, percentSeries],
      xAxisData: data.chart.xAxis,
      hasData: data.summary.total > 0,
    };
  }, [data, autoLabels, manualLabels, autoSeriesLabel, manualSeriesLabel, unresolvedLabels, unresolvedSeriesLabel]);

  return (
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
      <ChartContainer title={title} loading={loading}>
        {hasData ? (
          <LineChart
            sx={{
              [`& .MuiLineElement-series-${AUTO_DRY_SERIES_ID}`]: {
                strokeDasharray: "6 4",
              },
            }}
            xAxis={[
              {
                data: xAxisData,
                scaleType: "point",
                id: "x",
                valueFormatter: (value: string, ctx: AxisValueFormatterContext) => formatAxisDate(value, ctx.location),
                tickLabelStyle: { fontSize: 11, fill: theme.palette.text.secondary },
              },
            ]}
            yAxis={[
              {
                id: "count",
                tickLabelStyle: { fontSize: 12, fill: theme.palette.text.secondary },
              },
              {
                id: "percent",
                position: "right",
                min: 0,
                max: 100,
                valueFormatter: (v: number | null) => (v == null ? "" : `${v}%`),
                tickLabelStyle: { fontSize: 12, fill: theme.palette.text.secondary },
              },
            ]}
            series={series}
            height={340}
            slotProps={{
              legend: {
                direction: "horizontal",
                position: { vertical: "bottom", horizontal: "center" },
                sx: { fontSize: 11 },
              },
            }}
            margin={{ bottom: 60, right: 60 }}
          />
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1, minHeight: 300 }}>
            <Typography color="text.secondary">No data available</Typography>
          </Box>
        )}
      </ChartContainer>
    </Box>
  );
}
